import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { useRecoilValue } from "recoil";
import { z } from "zod";

import { LoanInterestRateUpdateOperationEnum, MonthlyCashflowResult } from "@/api/generated";
import { CustomButton } from "@/components/Button";
import { PropertyEditErrorSummary } from "@/components/property_edit/PropertyEditErrorSummary";
import { CustomForm } from "@/components/property_edit/PropertyEditInputs";
import { SelectedProperty } from "@/view_models/propertySelectors";

import { useSaveLoanInterestRate } from "../../../hooks/useSaveLoanInterestRate";

import { LoanConditionsRepair } from "./LoanConditionsRepair";

const schema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "適用開始年月を入力してください。"),
  rate: z
    .string()
    .min(1, "年利を入力してください。")
    .regex(/^\d+(\.\d+)?$/, "年利を数値で入力してください。")
    .refine((value) => Number(value) <= 100, "年利は0〜100%で入力してください。"),
});

interface Props {
  history?: MonthlyCashflowResult;
  onCancel: () => void;
  onSaved: () => void;
}

export const InterestRateEditor: React.FC<Props> = ({ history, onCancel, onSaved }) => {
  const selectedProperty = useRecoilValue(SelectedProperty);
  const { save, loading, error, errorReason, clearError } = useSaveLoanInterestRate();
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      month: dayjs(history?.month).format("YYYY-MM"),
      rate: history?.loan_interest_rate_percentage?.toString() ?? "",
    },
  });

  const onSubmit = async (data: z.infer<typeof schema>) => {
    const month = `${data.month}-01`;
    if (dayjs(month).isBefore(dayjs(selectedProperty.property.bought_at).startOf("month"))) {
      setError("month", { message: "適用開始年月は購入月以降にしてください。" });
      return;
    }
    const duplicate = selectedProperty.user_input_monthly_cashflow_results.some(
      (row) =>
        row.loan_interest_rate_percentage != null &&
        row.month !== history?.month &&
        dayjs(row.month).isSame(month, "month")
    );
    if (duplicate) {
      setError("month", { message: "この月の金利は登録済みです。既存の履歴から訂正してください。" });
      return;
    }
    const saved = await save({
      operation: history ? LoanInterestRateUpdateOperationEnum.Correct : LoanInterestRateUpdateOperationEnum.Register,
      month,
      original_month: history?.month,
      loan_interest_rate_percentage: Number(data.rate),
    });
    if (saved) onSaved();
  };

  return (
    <Box p={2} bgcolor="background.default" borderRadius={8} data-edit-form-panel>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box mb={2}>
          <Typography variant="body2">
            {history
              ? "入力ミスの訂正に使用します。過去の残債・返済額も再計算されます。"
              : "変更前の金利履歴を残し、指定した月から新しい金利を適用します。"}
          </Typography>
        </Box>
        <Controller
          name="month"
          control={control}
          render={(field) => (
            <CustomForm
              {...field}
              label="金利の適用開始年月"
              type="month"
              required
              disabled={loading}
              error={!!errors.month}
              errorText={errors.month?.message}
            />
          )}
        />
        <Controller
          name="rate"
          control={control}
          render={(field) => (
            <CustomForm
              {...field}
              label="借入利率（年利）"
              required
              disabled={loading}
              unitSuffix="%"
              isShort
              error={!!errors.rate}
              errorText={errors.rate?.message}
            />
          )}
        />
        {error && (
          <Box mb={2}>
            <Typography role="alert" color="error">
              {error}
            </Typography>
          </Box>
        )}
        <PropertyEditErrorSummary errors={errors} />
        <Grid container spacing={2} data-edit-form-actions>
          <Grid item xs={6}>
            <CustomButton customVariant="textPrimary" onClick={onCancel} disabled={loading} size="small" fullWidth>
              キャンセル
            </CustomButton>
          </Grid>
          <Grid item xs={6}>
            <CustomButton buttonSize="L" customVariant="filled" type="submit" disabled={loading} size="small" fullWidth>
              {history ? "訂正を保存" : "金利変更を登録"}
            </CustomButton>
          </Grid>
        </Grid>
      </form>
      <LoanConditionsRepair reason={errorReason} onRepaired={clearError} />
    </Box>
  );
};
